// AI workflows page (Forge). Source: the platform workflow editor
// (localhost:4321/en/ontology/admin, Workflows tab, full page mode, October 2026).

const fr = {
  seo: {
    title: 'Workflows IA TriDyme (Forge) | Automatiser audit, calcul, contrôle et publication',
    description: 'Forge enchaîne les actions de la plateforme — audit documentaire, analyse structurelle, contrôle, correctifs, fusion, publication — avec des agents IA et des validations humaines.',
  },
  hero: {
    badge: 'Forge · Workflows IA',
    title: 'Vos processus métier, ',
    highlight: 'exécutés de bout en bout',
    subtitle: 'Un workflow enchaîne les actions de la plateforme — audit documentaire, analyse structurelle, contrôle, correctifs, fusion, publication — avec des agents IA et une validation humaine à chaque étape engageante.',
    ctas: [
      { label: 'Demander une démo', to: '/contact/?subject=demo', style: 'primary' },
      { label: 'Les agents IA autonomes', to: '/solutions/agents-ia/', style: 'outline' },
    ],
    stats: [
      { value: '{{workflowsStatus}}', label: 'statut des workflows' },
    ],
    image: { src: '/img/platform/workflow', alt: 'Éditeur de workflows TriDyme : graphe d’un flux audit, analyse structurelle, contrôle, correctif, validation humaine et publication' },
  },
  sections: [
    {
      id: 'exemple',
      tag: 'Exemple',
      title: 'Audit → contrôle → publication',
      subtitle: 'Un flux réel de la plateforme : six nœuds, une boucle bornée et une validation humaine avant toute écriture dans le système du client.',
      blocks: [
        {
          type: 'steps',
          items: [
            { title: 'Audit documentaire', text: 'Lecture et structuration des documents du site.' },
            { title: 'Analyse structurelle', text: 'Lancée si l’audit réussit et que la structure est un pylône treillis.' },
            { title: 'Contrôle', text: 'Règles de contrôle : si le résultat est conforme et le score suffisant, le site passe à la validation.' },
            { title: 'Correctif déterministe', text: 'En cas d’anomalie bloquante, correction puis nouveau contrôle, au plus deux fois.' },
            { title: 'Validation humaine', text: 'Le flux attend qu’un ingénieur ait relu et validé la branche.' },
            { title: 'Publication', text: 'Le site est poussé vers la GED du client.' },
          ],
        },
      ],
    },
    {
      id: 'editeur',
      tone: 'light',
      tag: 'L’éditeur',
      title: 'Un graphe, pas une procédure',
      blocks: [
        {
          type: 'cards',
          columns: 3,
          items: [
            { icon: 'Forge', title: 'Graphe visuel', text: 'On glisse des nœuds, on les relie, on règle chaque action et ses paramètres.' },
            { icon: 'Bolt', title: 'Déclencheurs', text: 'Lancement sur une sélection de sites, ou à l’arrivée d’un événement ; chaque arête porte sa condition (par exemple « statut = succès ET type de structure = pylône treillis »).' },
            { icon: 'Loop', title: 'Boucles bornées', text: 'Contrôle → correctif → fusion → contrôle, avec un nombre maximal de passages : au-delà, l’exécution s’arrête « bloquée ».' },
            { icon: 'SyncAlt', title: 'Jointures', text: 'Attendre tous les prédécesseurs ou reprendre dès le premier.' },
            { icon: 'Lock', title: 'Versions et droits', text: 'Chaque flux est versionné, activable, et l’on choisit qui peut le lancer.' },
            { icon: 'History', title: 'Exécutions suivies', text: 'Chaque exécution est suivie site par site : avancement, statut, historique.' },
          ],
        },
      ],
    },
    {
      id: 'actions',
      tag: 'Bibliothèque d’actions',
      title: 'Les briques de vos workflows',
      subtitle: 'Chaque nœud est une action de la plateforme, avec ses paramètres et ses droits.',
      blocks: [
        {
          type: 'chips',
          items: ['Audit documentaire', 'Mise à jour de site', 'Analyse structurelle', 'Modèle BIM', 'Capacité sous scénario', 'Contrôle (règles)', 'Proposer un correctif (IA)', 'Appliquer un correctif (déterministe)', 'Fusionner la branche', 'Notifier', 'Pousser le site vers la GED', 'Ranger dans un ensemble', 'Créer une tâche de suivi', 'Devis de renfort', 'Validation du modèle structurel', 'Plans de fabrication des renforts'],
        },
        {
          type: 'cards',
          columns: 2,
          items: [
            { icon: 'Groups', kicker: 'Contrôle de flux', title: 'Validation humaine', text: 'Le flux attend une tâche : un ingénieur relit l’écart et décide.' },
            { icon: 'Agent', kicker: 'Contrôle de flux', title: 'Agent autonome', text: 'Le flux assigne une tâche à un agent IA et attend son résultat.', to: '/solutions/agents-ia/', linkLabel: 'Découvrir les agents IA' },
          ],
        },
      ],
    },
    {
      id: 'en-images',
      tone: 'light',
      tag: 'En images',
      title: 'Concevoir et suivre',
      blocks: [
        {
          type: 'screens',
          columns: 2,
          items: [
            { src: '/img/platform/workflow', title: 'Éditeur de workflows', caption: 'Bibliothèque d’actions, graphe du flux et paramètres de chaque nœud.', alt: 'Éditeur de workflows TriDyme' },
            { src: '/img/platform/forge', title: 'Exécutions', caption: 'Les flux disponibles et leurs exécutions, site par site.', alt: 'Liste des workflows et de leurs exécutions dans TriDyme' },
          ],
        },
      ],
    },
  ],
  cta: {
    title: 'Un premier workflow en production en {{workshopToProduction}}',
    text: 'Un workshop avec vos équipes, puis deux jours de construction sur vos données réelles.',
    ctas: [
      { label: 'Parler à un expert', to: '/contact/?subject=enterprise', style: 'black' },
      { label: 'Voir la plateforme', to: '/plateforme/', style: 'outline' },
    ],
  },
};

const en = {
  seo: {
    title: 'TriDyme AI workflows (Forge) | Automate audit, calculation, review and publication',
    description: 'Forge chains platform actions — document audit, structural analysis, review, fixes, merge, publication — with AI agents and human approvals.',
  },
  hero: {
    badge: 'Forge · AI workflows',
    title: 'Your business processes, ',
    highlight: 'run end to end',
    subtitle: 'A workflow chains platform actions — document audit, structural analysis, review, fixes, merge, publication — with AI agents and a human approval at every binding step.',
    ctas: [
      { label: 'Request a demo', to: '/contact/?subject=demo', style: 'primary' },
      { label: 'Autonomous AI agents', to: '/solutions/agents-ia/', style: 'outline' },
    ],
    stats: [
      { value: '{{workflowsStatus}}', label: 'workflow status' },
    ],
    image: { src: '/img/platform/workflow', alt: 'TriDyme workflow editor: graph of an audit, structural analysis, review, fix, human approval and publication flow' },
  },
  sections: [
    {
      id: 'exemple',
      tag: 'Example',
      title: 'Audit → review → publication',
      subtitle: 'A real flow from the platform: six nodes, a bounded loop and a human approval before anything is written to the client’s system.',
      blocks: [
        {
          type: 'steps',
          items: [
            { title: 'Document audit', text: 'The site’s documents are read and structured.' },
            { title: 'Structural analysis', text: 'Run when the audit succeeds and the structure is a lattice tower.' },
            { title: 'Review', text: 'Control rules: if the result is compliant and the score high enough, the site moves on to approval.' },
            { title: 'Deterministic fix', text: 'On a blocking anomaly, fix then review again, at most twice.' },
            { title: 'Human approval', text: 'The flow waits until an engineer has reviewed and approved the branch.' },
            { title: 'Publication', text: 'The site is pushed to the client’s document management system.' },
          ],
        },
      ],
    },
    {
      id: 'editeur',
      tone: 'light',
      tag: 'The editor',
      title: 'A graph, not a procedure',
      blocks: [
        {
          type: 'cards',
          columns: 3,
          items: [
            { icon: 'Forge', title: 'Visual graph', text: 'Drag nodes, connect them, set each action and its parameters.' },
            { icon: 'Bolt', title: 'Triggers', text: 'Run on a selection of sites or on an event; each edge carries its condition (e.g. “status = success AND structure type = lattice tower”).' },
            { icon: 'Loop', title: 'Bounded loops', text: 'Review → fix → merge → review, with a maximum number of passes: beyond it, the run stops as “blocked”.' },
            { icon: 'SyncAlt', title: 'Joins', text: 'Wait for every predecessor or resume on the first one.' },
            { icon: 'Lock', title: 'Versions and permissions', text: 'Each flow is versioned and can be enabled; you choose who may start it.' },
            { icon: 'History', title: 'Tracked runs', text: 'Every run is tracked site by site: progress, status, history.' },
          ],
        },
      ],
    },
    {
      id: 'actions',
      tag: 'Action library',
      title: 'The building blocks of your workflows',
      subtitle: 'Each node is a platform action, with its parameters and permissions.',
      blocks: [
        {
          type: 'chips',
          items: ['Document audit', 'Update site', 'Structural analysis', 'BIM model', 'Capacity under scenario', 'Review (rules)', 'Propose a fix (AI)', 'Apply fix (deterministic)', 'Merge branch', 'Notify', 'Push site to document system', 'Add to object set', 'Create follow-up task', 'Reinforcement quote', 'Structural model validation', 'Reinforcement fabrication drawings'],
        },
        {
          type: 'cards',
          columns: 2,
          items: [
            { icon: 'Groups', kicker: 'Flow control', title: 'Human approval', text: 'The flow waits on a task: an engineer reviews the diff and decides.' },
            { icon: 'Agent', kicker: 'Flow control', title: 'Autonomous agent', text: 'The flow assigns a task to an AI agent and waits for its outcome.', to: '/solutions/agents-ia/', linkLabel: 'Discover AI agents' },
          ],
        },
      ],
    },
    {
      id: 'en-images',
      tone: 'light',
      tag: 'Screenshots',
      title: 'Design and track',
      blocks: [
        {
          type: 'screens',
          columns: 2,
          items: [
            { src: '/img/platform/workflow', title: 'Workflow editor', caption: 'Action library, flow graph and the parameters of each node.', alt: 'TriDyme workflow editor' },
            { src: '/img/platform/forge', title: 'Runs', caption: 'Available flows and their runs, site by site.', alt: 'List of workflows and their runs in TriDyme' },
          ],
        },
      ],
    },
  ],
  cta: {
    title: 'A first workflow in production in {{workshopToProduction}}',
    text: 'A workshop with your teams, then two days of building on your real data.',
    ctas: [
      { label: 'Talk to an expert', to: '/contact/?subject=enterprise', style: 'black' },
      { label: 'See the platform', to: '/plateforme/', style: 'outline' },
    ],
  },
};

module.exports = { fr, en };
